import { useState, useEffect, MutableRefObject, useContext } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";
import Link from "next/link";
import ReactTimeAgo from "react-time-ago";
import { UserContext } from "../../context/userContext";

const ReviewContainer = styled(Paper)(({ theme }) => ({
  padding: "1rem",
  marginBottom: "1rem",
  boxShadow: "0 8px 16px 0 rgb(63 46 28 / 25%)",
  borderRadius: "8px",
  flexDirection: "column",
  gap: "1.5rem",
  maxWidth: "340px",
  display: "none",
  position: "fixed",
  zIndex: 2147483001,
  top: "374px",
  transition: "top 0.2s ease-out 0s",

  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

type ReviewStarsProps = {
  rating: number;
};

const ReviewStars = (props: ReviewStarsProps) => {
  const stars = [];
  for (let i = 0; i < props.rating; i++) {
    stars.push(
      <StarIcon
        key={i}
        fontSize="small"
        sx={{ color: "#FFC107", margin: "0 0 0 -2px", p: 0 }}
      />
    );
  }
  return <Box>{stars}</Box>;
};

export type TutorHighlightedReviewProps = {
  tutorId: string;
  tutorName?: string;
  feedback: {
    comment: string;
    submittedDate: string;
    rating: number;
  };
  parent?: {
    firstName?: string;
    homeAddressTown?: string;
  };
  student?: {
    firstName?: string;
  };
  position?: number;
  containerRef?: MutableRefObject<HTMLDivElement>;
};
type positionCalcProps = {
  scrollPosition: number;
  containerRef: MutableRefObject<HTMLDivElement>;
  firstLoad: boolean;
  position: number;
};
function positionCalc(props: positionCalcProps) {
  const { scrollPosition, containerRef, firstLoad, position } = props;
  let parentTop: number;
  if (containerRef) {
    parentTop = containerRef.current.getBoundingClientRect().top;
  } else {
    parentTop = 374 - scrollPosition;
  }
  if (firstLoad) {
    parentTop = 374;
  }
  const top = parentTop + position * 182;

  return `${top}px`;
}

export const TutorHighlightedReview = (props: TutorHighlightedReviewProps) => {
  const {
    tutorId,
    tutorName,
    feedback,
    parent,
    student,
    position,
    containerRef,
  } = props;
  const [scrollPosition, setScrollPosition] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    setFirstLoad(false);

    const handleScroll = () => {
      const userScrollPosition = window.scrollY;
      setScrollPosition(userScrollPosition);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const { isAuthenticated } = useContext(UserContext);

  const tutorMsgURL = isAuthenticated
    ? `${process.env.NEXT_PUBLIC_ZEUS_URL}/parents/secure/contact-a-tutor.html?id=${tutorId}`
    : `${process.env.NEXT_PUBLIC_ZEUS_URL}/tutors/${tutorId}`;

  if (!tutorId || !feedback) {
    return null;
  }

  const reviewerName = parent?.firstName || student?.firstName;

  return (
    <ReviewContainer
      className="highlighted-review"
      style={{
        top: positionCalc({
          scrollPosition,
          containerRef,
          firstLoad,
          position,
        }),
      }}
    >
      <Box className="review-header">
        <Box className="reviewer-details">
          <Typography
            variant="text1"
            component="strong"
            sx={{ fontWeight: 600 }}
          >
            {reviewerName ? reviewerName : ""} &nbsp;
          </Typography>
          <Typography
            variant="text1"
            component="span"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {parent?.homeAddressTown && `from ${parent.homeAddressTown}`}
          </Typography>
        </Box>
        <Box
          className="reviewer-rating"
          sx={{ display: "flex", alignContent: "center", gap: "0.5rem" }}
        >
          {!!feedback.rating && <ReviewStars rating={feedback.rating} />}
          <Typography variant="text2" component="span">
            {!!feedback.submittedDate && (
              <ReactTimeAgo
                date={new Date(feedback.submittedDate)}
                locale="en-GB"
              />
            )}
          </Typography>
        </Box>
      </Box>
      <Box className="review-body">
        <Typography variant="text1" component="p">
          &quot;{" "}
          {feedback.comment.length > 300
            ? feedback.comment.slice(0, 300) + "..."
            : feedback.comment}{" "}
          &quot;
        </Typography>
        <Link
          href={`${process.env.NEXT_PUBLIC_ZEUS_URL}/tutors/${tutorId}/#reviews`}
          style={{ textDecoration: "underline" }}
        >
          Show more reviews
        </Link>
      </Box>
      <Box className="review-footer">
        <Button
          href={tutorMsgURL}
          variant="contained"
          color="primary"
          size="small"
          fullWidth
        >
          <span
            style={{
              display: "block",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              height: "1.1em",
            }}
          >
            Message&nbsp;{tutorName && `${tutorName}`}
          </span>
        </Button>
      </Box>
    </ReviewContainer>
  );
};

export default TutorHighlightedReview;
